import React, { useState, useEffect, useCallback } from 'react';
import SearchIcon from '@rsuite/icons/Search';
import debounce from 'lodash/debounce';
import PlusIcon from '@rsuite/icons/Plus';
import api from '../../services/api';
import '../style.css';
import { Container as ContainerSuite, InputGroup, Stack, Table } from 'rsuite';
import { useAuth } from '../../hooks/auth';
import EditarCadastrarUsuario from './EditarCadastrar';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { CustomSelectPicker } from '../../components/CustomSelectPicker';
import { CustomInput } from '../../components/CustomInput';
import { CustomButton } from '../../components/CustomButton';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { CustomCell } from '../../components/CustomCell';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import ResetarSenhaUsuario from './ResetarSenha';
import { FaUserAlt } from 'react-icons/fa';

const { Column } = Table;

export default function Usuarios() {
  const { id_empresa } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [modal, setModal] = useState(false);
  const [modalReset, setModalReset] = useState(false);
  const [gruposMap, setGruposMap] = useState({});
  const { theme } = useCustomTheme();

  const carregarGrupos = useCallback(async () => {
    const { data } = await api.get(`grupo-acesso/listar/${id_empresa}`);
    const mapa = {};
    data.forEach((g) => { mapa[g.Id] = g.Nome; });
    setGruposMap(mapa);
  }, [id_empresa]);

  const atualizar = async () => {
    await api
      .get(`gestor/listar/${id_empresa}`)
      .then((res) => {
        setUsuarios(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const consultar = async (consulta) => {
    consulta = consulta.substring(4);
    if (consulta === '') {
      atualizar();
    } else {
      await api
        .post('gestor/listarPorNomeGestor', {
          IdEmpresa: id_empresa,
          Consulta: consulta,
        })
        .then((res) => {
          res.data.length > 0 ? setUsuarios(res.data) : setUsuarios([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const dataAtivos = [
    { label: 'Ver todos', value: '' },
    { label: 'Filtrar: Ativos', value: 1 },
    { label: 'Filtrar: Inativos', value: 0 },
  ];

  const debounceSearch = debounce((valor) => {
    let instrucao = '';
    const value_lower = valor?.toLowerCase();

    instrucao += filtroStatus !== '' ? ` AND Ativo='${filtroStatus}'` : ``;
    instrucao += value_lower ? ` AND LOWER(Nome) LIKE '%${value_lower}%'` : ``;
    consultar(instrucao);
    setBuscar(valor);
  }, 1000);

  useEffect(() => {
    debounceSearch(buscar ?? '');
  }, [filtroStatus]);

  useEffect(() => {
    atualizar();
    carregarGrupos();
  }, []);

  return (
    <ContainerSuite
      style={{
        padding: '10px',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background,
      }}
    >
      <ContentHeader
        title="Usuários"
        subtitle="Gerencie os usuários da sua empresa"
        icon={FaUserAlt}
        secondComponent={
          <InputGroup inside style={{ width: '36vw', border: 'none' }}>
            <CustomInput onChange={debounceSearch} />
            <InputGroup.Addon>
              <SearchIcon color={theme.colors.textSecondary} />
            </InputGroup.Addon>
          </InputGroup>
        }
        thirdComponent={
          <Stack spacing={10}>
            <CustomSelectPicker
              data={dataAtivos}
              defaultValue={'Ativos'}
              value={filtroStatus}
              onChange={(value) => setFiltroStatus(value)}
              style={{ width: 140 }}
            />
            <CustomButton
              style={{ marginRight: 10 }}
              appearance="primary"
              onClick={() => {
                setModal(true);
                setUsuario(null);
              }}
            >
              <PlusIcon style={{ marginRight: 6 }} />
              Cadastrar Usuário
            </CustomButton>
            <DuvidasButton tela="usuario" />
          </Stack>
        }
      />

      <Table
        height={window.innerHeight - 60 - 90}
        bordered={true}
        loading={loading}
        data={usuarios}
        width={'100%'}
        autoHeight
        style={{
          overflowY: 'auto',
          borderColor: theme.colors.backgroundSecondary,
          backgroundColor: theme.colors.backgroundSecondary,
          color: theme.colors.textSecondary,
        }}
        locale={{
          emptyMessage: 'Nenhum usuário encontrado',
          loading: 'Carregando...',
        }}
      >
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          align="center"
          fixed
        >
          <CustomHeaderCell>Código</CustomHeaderCell>
          <CustomCell dataKey="Id" />
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={1}
        >
          <CustomHeaderCell>Nome</CustomHeaderCell>
          <CustomCell dataKey="Nome" />
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={1}
        >
          <CustomHeaderCell>Login</CustomHeaderCell>
          <CustomCell dataKey="Login" />
        </Column>
        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          flexGrow={1}
          minWidth={140}
        >
          <CustomHeaderCell>Grupo de Acesso</CustomHeaderCell>
          <CustomCell>
            {(rowData) => (
              <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                {gruposMap[rowData.IdGrupoAcesso] || '—'}
              </span>
            )}
          </CustomCell>
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          width={100}
          fixed="right"
        >
          <CustomHeaderCell>Ativo</CustomHeaderCell>
          <CustomCell dataKey="Fim">
            {(rowData) => (
              <span style={{ padding: 0 }}>
                {rowData.Ativo ? (
                  <CustomButton
                    appearance="primary"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 10px',
                    }}
                  >
                    Sim
                  </CustomButton>
                ) : (
                  <CustomButton
                    appearance="ghost"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 10px',
                    }}
                  >
                    Não
                  </CustomButton>
                )}
              </span>
            )}
          </CustomCell>
        </Column>

        <Column
          style={{
            background: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          width={210}
          fixed="right"
        >
          <CustomHeaderCell>Opções</CustomHeaderCell>
          <CustomCell>
            {(rowData) => (
              <>
                <CustomButton
                  color={theme.colors.warning}
                  appearance="ghost"
                  size="xs"
                  onClick={() => {
                    setUsuario(rowData);
                    setModal(true);
                  }}
                >
                  {' '}
                  Editar{' '}
                </CustomButton>
                <CustomButton
                  style={{ marginLeft: 4 }}
                  appearance="default"
                  size="xs"
                  onClick={() => {
                    setUsuario(rowData);
                    setModalReset(true);
                  }}
                >
                  Resetar Senha
                </CustomButton>
              </>
            )}
          </CustomCell>
        </Column>
      </Table>
      {modal && (
        <EditarCadastrarUsuario
          modal={modal}
          close={() => setModal(false)}
          usuario={usuario}
          setUsuarios={setUsuarios}
          id_empresa={id_empresa}
        />
      )}
      {modalReset && <ResetarSenhaUsuario
        modal={modalReset}
        close={()=> setModalReset(false)}
        usuario={usuario}
      />}
    </ContainerSuite>
  );
}
